import { useEffect, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";
import {
  clubChannel,
  type ClientMessage,
  type ServerMessage,
} from "@plotlounge/shared/websocket";
import { env } from "../../env";

const PING_INTERVAL_MS = 30_000;
const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;

/**
 * Subscribe to a single club's WebSocket channel for the lifetime of the
 * component. Manages its own socket — connect on mount, ping/keepalive,
 * exponential-backoff reconnect on drop, intentional close on unmount.
 *
 * Receives every typed `ServerMessage` for the channel (excluding
 * `pong`/`subscribed`/etc., which are filtered out). The caller's `onEvent`
 * is called with the latest reference each render, so it doesn't need to
 * be memoized.
 */
export function useClubChannel(
  clubId: string | null | undefined,
  onEvent: (event: ServerMessage) => void,
): void {
  const { isSignedIn, getToken } = useAuth();
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!clubId || !isSignedIn) return;

    let ws: WebSocket | null = null;
    let pingInterval: ReturnType<typeof setInterval> | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
    let intentionalClose = false;
    const channel = clubChannel(clubId);

    const send = (message: ClientMessage) => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(message));
      }
    };

    const clearTimers = () => {
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
    };

    const connect = async () => {
      const token = await getToken();
      if (!token) return;

      const wsUrl = env.apiBaseUrl
        .replace(/^https:/, "wss:")
        .replace(/^http:/, "ws:")
        .replace(/\/api$/, "");
      const url = `${wsUrl}/ws?token=${token}`;

      try {
        ws = new WebSocket(url);
      } catch {
        return;
      }

      ws.onopen = () => {
        reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
        pingInterval = setInterval(() => {
          send({ type: "ping" });
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        let message: ServerMessage;
        try {
          message = JSON.parse(event.data as string) as ServerMessage;
        } catch {
          return;
        }

        if (message.type === "connected") {
          send({ type: "subscribe", channel });
          return;
        }
        if (message.type === "pong") return;
        if (message.type === "subscribed") return;
        if (message.type === "unsubscribed") return;

        try {
          onEventRef.current(message);
        } catch {
          // Caller threw — swallow so a buggy listener doesn't kill the socket
        }
      };

      ws.onclose = () => {
        clearTimers();
        ws = null;

        if (intentionalClose) return;

        const jitter = Math.random() * 1000;
        const delay = reconnectDelay + jitter;
        reconnectTimeout = setTimeout(() => {
          connect();
        }, delay);
        reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY_MS);
      };

      ws.onerror = () => {
        // onclose handles reconnect — nothing to do here
      };
    };

    connect();

    return () => {
      intentionalClose = true;
      clearTimers();
      if (ws) {
        ws.close(1000, "Component unmount");
        ws = null;
      }
    };
  }, [clubId, isSignedIn, getToken]);
}
